import React, { useState } from 'react';
import DataTable from "components/dataTable/DataTable";
import { IVariant, TInventoryLevelLog } from 'redux/reducers/models/product';
import { Badge } from 'components/badge/Badge';
import { t } from 'i18next';
import moment from 'moment';
import Input from 'components/form/input';
import { ButtonNew } from 'components/buttonNew';
import useCurrencyFacility from 'hooks/useCurrency/useCurrencyFacility';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose, faPenToSquare } from '@fortawesome/pro-regular-svg-icons';
import { useAppDispatch } from 'hooks/redux';
import { showError, showSuccess } from 'redux/actions/ui';
import { PutInventoryLevelLog } from 'api/rpc/2024-04/facilityAdmin/product/inventory';
import { StatusCode } from 'api/protocols';

export default function InventoryHistoryTable(props: {
  variant: IVariant,
  adjustQuantity: boolean,
  unit: string,
  onSaveAdjustment: (newQuantity: { price: number, cost: number, quantity: number }) => void,
  onCancelAdjustment: () => void,
}) {
  const [newAdjustment, setNewAdjustment] = useState({
    price: props.variant?.inventory_item?.inventory_level?.price_override,
    cost: props.variant?.inventory_item?.inventory_level?.cost_override,
    quantity: '',
  });
  const [editingLog, setEditingLog] = useState<number | null>(null);
  const [editedLogCost, setEditedLogCost] = useState(0);
  const [updatedLogCosts, setUpdatedLogCosts] = useState<Record<number, number>>({});
  const { displayCurrency } = useCurrencyFacility();
  const dispatch = useAppDispatch();

  function handleSave() {
    const cost = Number(newAdjustment.cost);
    if (cost < 0) {
      dispatch(showError(t('Cost must be greater than or equal to 0')));
      return;
    }

    props.onSaveAdjustment({
      price: Number(newAdjustment.quantity) > 0 && newAdjustment.price ? Number(newAdjustment.price) : undefined,
      cost: Number(newAdjustment.quantity) > 0 && newAdjustment.cost ? Number(newAdjustment.cost) : undefined,
      quantity: Number(newAdjustment.quantity),
    });
  }

  function handleEditLog(log: TInventoryLevelLog) {
    setEditingLog(log.id);
    setEditedLogCost(updatedLogCosts[log.id] ?? log.cost);
  }

  async function handleSaveLog() {
    if (editingLog === null) {
      return;
    }
    const resp = await PutInventoryLevelLog({
      inventory_level_log_id: editingLog,
      cost: editedLogCost,
    }, false);

    if (resp.status !== StatusCode.OK) {
      dispatch(showError(t('Error updating inventory log')));
      return;
    }

    dispatch(showSuccess(t('Updated inventory log')));
    setUpdatedLogCosts(prev => ({ ...prev, [editingLog]: editedLogCost }));
    setEditedLogCost(0);
    setEditingLog(null);
  }

  return <DataTable
    columns={[
      {
        id: 'date',
        label: 'Date',
      },
      {
        id: 'qty-received',
        label: 'Qty received',
      },
      {
        id: 'qty-consumed',
        label: 'Qty consumed',
      },
      {
        id: 'costs',
        label: 'Cost per unit ($)',
      },
      {
        id: 'po',
        label: 'PO#',
      },
      {
        id: 'type',
        label: 'Type',
      },
      ...(props.adjustQuantity ? [{ label: '' }] : []),
    ]}>
    {props.adjustQuantity && <tr key="new">

      <td>{moment().format('MMM DD, YYYY h:mm A')}</td>
      <td>
        <Input
          id="new-quantity"
          onChange={e => setNewAdjustment({ ...newAdjustment, quantity: e.target.value })}
        />
      </td>
      <td></td>
      <td>
        {Number(newAdjustment.quantity) > 0 && <Input
          id="new-cost"
          onChange={e => setNewAdjustment({ ...newAdjustment, cost: Number(e.target.value) })}
          type="number"
          value={newAdjustment.cost}
          min={0}
          step="0.01"
        />}
      </td>

      <td>{t('N/A')}</td>
      <td>
        <Badge type={'lightblue'}>
          {t('Adjustment')}
        </Badge>
      </td>
      <td>
        <div className="flex">
          <ButtonNew
            size="small"
            type="tertiary"
            className="px-0"
            onClick={handleSave}
          >
            {t('Save')}
          </ButtonNew>
          <ButtonNew
            size="small"
            type="tertiary"
            className="px-0"
            onClick={() => props.onCancelAdjustment()}
          >
            <FontAwesomeIcon icon={faClose} />
          </ButtonNew>
        </div>
      </td>
    </tr>}
    {!props.adjustQuantity && props.variant?.inventory_item?.inventory_level?.inventory_level_logs?.length < 1 && <tr>
      <td colSpan={8} align="center">{t('No inventory history')}</td>
    </tr>}
    {props.variant?.inventory_item?.inventory_level?.inventory_level_logs?.map(log => <tr key={log.id}>
      <td>{moment(log.created_at).format('MMM DD, YYYY h:mm A')}</td>
      <td>{log.change} {props.unit}</td>
      <td>{log?.consumed_quantity ?? '--'} {props.unit}</td>
      <td>
        {editingLog === log.id
          ? <div className="flex gap-1 items-center">
            <Input
              id="new-cost"
              onChange={e => setEditedLogCost(Number(e.target.value))}
              type="number"
              value={editedLogCost ?? ""}
              min={0}
              step="0.01"
            />
            <ButtonNew
              size="small"
              type="tertiary"
              className="px-0"
              onClick={handleSaveLog}
            >
              {t('Save')}
            </ButtonNew>
            <ButtonNew
              size="small"
              type="tertiary"
              className="px-0 ml-0"
              onClick={() => setEditingLog(null)}
            >
              <FontAwesomeIcon icon={faClose} />
            </ButtonNew>
          </div>
          : <div className="flex gap-1 items-center">
            {displayCurrency(updatedLogCosts[log.id] ?? log.cost)}
            <ButtonNew
              size="small"
              type="tertiary"
              className="px-0"
              onClick={() => handleEditLog(log)}
            >
              <FontAwesomeIcon icon={faPenToSquare} />
            </ButtonNew>
          </div>}
      </td>
      <td>{log.inventory_record?.po_number ?? t('N/A')}</td>
      <td>
        <Badge type={
          (log.type === 'received' && 'success') 
            || (log.type === 'full_count_adjustment' && 'warning') 
            || (log.type === 'partial_count_adjustment' && 'warning') 
            || 'lightblue'
        }>
          {log.type === 'manual_adjustment' && t('Adjustment')}
          {log.type === 'log_created' && t('Adjustment')}
          {log.type === 'received' && t('Received')}
          {log.type === 'full_count_adjustment' && t('Full Count Adjustment')}
          {log.type === 'partial_count_adjustment' && t('Partial Count Adjustment')}
        </Badge>
      </td>
      {props.adjustQuantity && <td></td>}
    </tr>)}
  </DataTable>;
}
